import * as THREE from 'three';
import { SpriteNodeMaterial } from 'three/webgpu';

type SpriteMaterialRestoreEntry = {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
};

export type SceneDepthRenderOptions = {
  scene: THREE.Scene;
  camera: THREE.Camera;
  sceneDepthLayerMask: number;
  depthMaterial: THREE.Material;
  renderScene: (profileName: string, overrideMaterial: THREE.Material | null) => void;
  depthProfileName?: string;
  spriteProfileName?: string;
};

/**
 * Renders scene depth with alpha-tested sprites handled as cutout occluders.
 */
export class SceneDepthRenderer {
  private spriteOccluderMaterialCache: Map<string, SpriteNodeMaterial> = new Map();

  @THREE.profile
  public render(options: SceneDepthRenderOptions): void {
    const {
      scene,
      camera,
      sceneDepthLayerMask,
      depthMaterial,
      renderScene,
      depthProfileName = 'depth pass',
      spriteProfileName = 'depth sprite pass',
    } = options;

    const savedLayerMask = camera.layers.mask;
    try {
      camera.layers.mask = sceneDepthLayerMask;

      const alphaOccluderSprites = this.collectAlphaOccluderSprites(scene, sceneDepthLayerMask);
      try {
        for (const sprite of alphaOccluderSprites) {
          sprite.visible = false;
        }
        renderScene(depthProfileName, depthMaterial);
      } finally {
        for (const sprite of alphaOccluderSprites) {
          sprite.visible = true;
        }
      }

      if (alphaOccluderSprites.length > 0) {
        this.renderAlphaOccluderSprites(scene, alphaOccluderSprites, renderScene, spriteProfileName);
      }
    } finally {
      camera.layers.mask = savedLayerMask;
    }
  }

  private collectAlphaOccluderSprites(scene: THREE.Scene, sceneDepthLayerMask: number): THREE.Sprite[] {
    const alphaOccluderSprites: THREE.Sprite[] = [];
    if (sceneDepthLayerMask === 0) return alphaOccluderSprites;

    scene.traverseVisible((object: THREE.Object3D) => {
      if (!(object instanceof THREE.Sprite)) return;
      if ((object.layers.mask & sceneDepthLayerMask) === 0) return;
      const spriteMaterial = object.material;
      if (!spriteMaterial) return;
      const hasAlphaTexture = spriteMaterial.map !== null || spriteMaterial.alphaMap !== null;
      const needsAlphaCutout = spriteMaterial.transparent || spriteMaterial.opacity < 1 || (spriteMaterial.alphaTest ?? 0) > 0;
      if (!hasAlphaTexture || !needsAlphaCutout) return;
      alphaOccluderSprites.push(object);
    });

    return alphaOccluderSprites;
  }

  private renderAlphaOccluderSprites(
    scene: THREE.Scene,
    alphaOccluderSprites: THREE.Sprite[],
    renderScene: (profileName: string, overrideMaterial: THREE.Material | null) => void,
    profileName: string,
  ): void {
    const hiddenForSpritePass: THREE.Object3D[] = [];
    const spriteMaterialSwaps: SpriteMaterialRestoreEntry[] = [];

    try {
      const alphaSpriteSet = new Set(alphaOccluderSprites);
      scene.traverseVisible((object: THREE.Object3D) => {
        if (alphaSpriteSet.has(object as THREE.Sprite)) return;
        if (object instanceof THREE.Light) return;
        if (!('material' in object)) return;
        object.visible = false;
        hiddenForSpritePass.push(object);
      });

      for (const sprite of alphaOccluderSprites) {
        spriteMaterialSwaps.push({ sprite, material: sprite.material });
        sprite.material = this.getSpriteOccluderMaterial(sprite.material);
      }

      renderScene(profileName, null);
    } finally {
      for (const { sprite, material } of spriteMaterialSwaps) {
        sprite.material = material;
      }
      for (const object of hiddenForSpritePass) {
        object.visible = true;
      }
    }
  }

  private getSpriteOccluderMaterial(material: THREE.SpriteMaterial): SpriteNodeMaterial {
    const map = material.map ?? null;
    const alphaMap = material.alphaMap ?? null;
    const alphaTest = Math.max(0.0001, material.alphaTest ?? 0);
    const cacheKey = `${map?.uuid ?? 'none'}:${alphaMap?.uuid ?? 'none'}:${alphaTest.toFixed(4)}`;
    let spriteMaterial = this.spriteOccluderMaterialCache.get(cacheKey);
    if (!spriteMaterial) {
      spriteMaterial = new SpriteNodeMaterial({
        ...(map ? { map } : {}),
        ...(alphaMap ? { alphaMap } : {}),
        color: 0x000000,
        transparent: false,
        alphaTest,
      });
      spriteMaterial.depthTest = true;
      spriteMaterial.depthWrite = true;
      spriteMaterial.fog = false;
      spriteMaterial.toneMapped = false;
      this.spriteOccluderMaterialCache.set(cacheKey, spriteMaterial);
    }

    return spriteMaterial;
  }

  public dispose(): void {
    for (const mat of this.spriteOccluderMaterialCache.values()) {
      mat.dispose();
    }
    this.spriteOccluderMaterialCache.clear();
  }
}
